import React, { useEffect, useState } from "react";
import {
  getApprovalStats,
  getApprovals,
  decideApproval,
  bulkApprove,
  autoApproveLowRisk,
} from "../../adminAPI/adminApprovalsApi";
import Pagination from "../../components/common/Pagination";
import { formatRelativeDate } from "../../utils/dateUtils";
import { getPriorityBadgeClass, getStatusBadgeClass } from "../../utils/badgeUtils";


const ApprovalsKYC = () => {
  const token = localStorage.getItem("otoken");

  /* ================= STATE ================= */
  const [stats, setStats] = useState({});
  const [approvals, setApprovals] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selected, setSelected] = useState([]);

   const [filters, setFilters] = useState({
    search: "",
    type: "",
    priority: "",
    status: "",
    age: "",
  });

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 10;

  /* ================= LOADERS ================= */
  const loadStats = async () => {
    const data = await getApprovalStats(token);
    setStats(data);
  };

  const loadApprovals = async () => {
    const data = await getApprovals(
      { ...filters, page, limit: PAGE_SIZE },
      token
    );
    setApprovals(data.rows);
    setTotal(data.total);
  };

  useEffect(() => {
    loadStats();
    loadApprovals();
  }, [filters, page]);

  const handleFilterChange = (e) => {
    setFilters(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setPage(1);
  };


  /* ================= ACTIONS ================= Actions (Approve / Reject / Bulk / Auto)*/

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDecision = async (id, status) => {
    await decideApproval(id, status, token);
    loadApprovals();
    loadStats();
  };

  const handleBulkApprove = async () => {
    if (!selectedIds.length) return;
    await bulkApprove(selectedIds, token);
    setSelectedIds([]);
    loadApprovals();
    loadStats();
  };

  const handleAutoApprove = async () => {
    await autoApproveLowRisk(token);
    loadApprovals();
    loadStats();
  };

  /* ================= UI ================= */
  return (
    <>
      {/* <!-- Page header --> */}
        <div className="page-header mb-4 d-flex flex-wrap justify-content-between align-items-center" style={{ marginLeft: "30px", marginRight: "30px" }}>
          <div className="mb-3 mb-md-0">
            <h1 className="h4 mb-1">Approvals & KYC</h1>
            <p className="text-muted mb-0">
              Review pending KYC verifications, job approvals, and document submissions.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-sm btn-outline-secondary">
              <i className="bi-download me-1"></i> Export
            </button>
            <button className="btn btn-sm btn-warning text-white">
              <i className="bi-clock me-1"></i> Auto-approve low risk (12)
            </button>
          </div>
        </div>

        {/* <!-- Stats cards --> */}
        <div className="row g-3 mb-4" style={{ marginLeft: "30px", marginRight: "30px" }}>
          <div className="col-md-3">
            <div className="card stats-card shadow-sm">
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted text-uppercase">Total Pending</div>
                  <div className="h5 mb-0">{stats.total_pending}</div>
                  <small className="text-danger">
                    <i className="bi-exclamation-triangle"></i> +3 today
                  </small>
                </div>
                <div className="stats-icon bg-danger text-white">
                  <i className="bi-pending"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card stats-card shadow-sm">
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted text-uppercase">Employer KYC</div>
                  <div className="h5 mb-0">{stats.employer_kyc}</div>
                  <small className="text-warning">
                    <i className="bi-file-earmark-check"></i> 8 high priority
                  </small>
                </div>
                <div className="stats-icon bg-warning text-white">
                  <i className="bi-building-check"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card stats-card shadow-sm">
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted text-uppercase">Job Approvals</div>
                  <div className="h5 mb-0">{stats.job_approvals}</div>
                  <small className="text-primary">
                    <i className="bi-briefcase-check"></i> Auto-approved: 3
                  </small>
                </div>
                <div className="stats-icon bg-primary text-white">
                  <i className="bi-briefcase"></i>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card stats-card shadow-sm">
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <div className="small text-muted text-uppercase">Overdue</div>
                  <div className="h5 mb-0">{stats.overdue}</div>
                  <small className="text-danger">
                    <i className="bi-clock-history"></i> 48 hours
                  </small>
                </div>
                <div className="stats-icon bg-secondary text-white">
                  <i className="bi-exclamation-octagon"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <!-- Filters --> */}
        <div className="card shadow-sm mb-4" style={{ marginLeft: "30px", marginRight: "30px" }}>
          <div className="card-body">
            <form className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Search</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white border-end-0">
                    <i className="bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-start-0"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Company, candidate, job title..."
                  />
                </div>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Type</label>
                <select className="form-select form-select-sm"
                        name="type"
                        value={filters.type}
                        onChange={handleFilterChange}
                >
                  <option value="">All</option>
                  <option value="EMPLOYER_KYC">Employer KYC</option>
                  <option value="JOB_APPROVAL">Job Approval</option>
                  <option value="CANDIDATE_VERIFICATION">Candidate Verification</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Priority</label>
                <select className="form-select form-select-sm"
                  name="priority"
                  value={filters.priority}
                  onChange={handleFilterChange}>
                  <option value="">All</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Status</label>
                <select 
                  className="form-select form-select-sm"
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  >
                  <option value="">All</option>
                  <option value="Pending">Pending</option>
                  <option value="In Review">In Review</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Age</label>
                <select 
                  className="form-select form-select-sm"
                  name="age"
                  value={filters.age}
                  onChange={handleFilterChange}
                  >
                  <option value="">All</option>
                  <option value="today">Today</option>
                  <option value="7d">7 days</option>
                  <option value="30d">30 days</option>
                </select>
              </div>
              <div className="col-md-1">
                <button type="submit" className="btn btn-sm btn-primary w-100">
                  Filter
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* <!-- Approvals Queue Table --> */}
        <div className="card shadow-sm" style={{ marginLeft: "30px", marginRight: "30px" }}>
          <div className="card-header d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Approval Queue</h6>
            <div>
              <span className="badge bg-danger-subtle text-danger me-2">27 pending</span>
              <button className="btn btn-sm btn-outline-success">
                <i className="bi-check-all me-1"></i> Bulk Approve
              </button>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th style={{width: "50px"}}>
                    <input
                      style={{marginTop: "-5px", marginLeft: "8px"}}
                     className="form-check-input" type="checkbox" id="selectAllApprovals" />
                  </th>
                  <th>Type</th>
                  <th>Details</th>
                  <th>Priority</th>
                  <th>Submitted</th>
                  <th>Documents</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {approvals.map((row) => (
    <tr key={row.approval_id}>
      <td>
        <input
          className="form-check-input"
          style={{marginTop: "-5px", marginLeft: "8px"}}
          type="checkbox"
          checked={selected.includes(row.approval_id)}
          onChange={() => toggleSelect(row.approval_id)}
        />
      </td>

      <td>
        <div className="fw-semibold">
            {(row.approval_type || "").replace(/_/g, " ")}
        </div>
        <small>{row.entity_name || "—"}</small>

      </td>

      <td>
        <div>{row.details}</div>
        <small className="text-muted">{row.entity_code}</small>
      </td>

      <td>
        <span className={getPriorityBadgeClass(row.priority)}>
          {row.priority}
        </span>

      </td>

      <td>{formatRelativeDate(row.created_at)}</td>

      <td>
        <span className="badge bg-warning-subtle text-warning border">
          {row.documents_completed}/{row.documents_required}
        </span>
      </td>

      <td>
        <span className={getStatusBadgeClass(row.status)}>
          {row.status}
        </span>
      </td>

      {/* <td className="text-end">
        <button className="btn btn-sm btn-outline-primary me-1">
          <i className="bi-eye"></i>
        </button>

        <div className="btn-group btn-group-sm">
          <button
            className="btn btn-success"
            onClick={() => handleDecision(row.approval_id, "Approved")}
          >
            <i className="bi-check-lg"></i>
          </button>
          <button
            className="btn btn-danger"
            onClick={() => handleDecision(row.approval_id, "Rejected")}
          >
            <i className="bi-x-lg"></i>
          </button>
        </div>
      </td> */}
     <td className="text-end">
  {/* View */}
  <button
    className="btn btn-sm btn-outline-primary me-1"
    title="View"
  >
    <i className="fa fa-solid fa-eye"></i>
  </button>

  {/* Approve */}
  <button
    className="btn btn-sm btn-success me-1"
    title="Approve"
    onClick={() => handleDecision(row.approval_id, "Approved")}
  >
    <i className="fa fa-solid fa-check"></i>
  </button>

  {/* Reject */}
  <button
    className="btn btn-sm btn-outline-danger"
    title="Reject"
    onClick={() => handleDecision(row.approval_id, "Rejected")}
  >
    <i className="fa fas fa-times"></i>
  </button>
</td>

    </tr>
  ))}
                  
                
                {/* <!-- Additional approval rows --> */}
              </tbody>
            </table>
          </div>

          
          {/* <!-- Pagination --> */}
           <Pagination
            page={page}
            limit={PAGE_SIZE}
            total={total}
            label="Approvals"
            onPageChange={setPage}
          />
        </div>
    </>
  );
};

const StatCard = ({ title, value, danger }) => (
  <div className="col-md-3">
    <div className="card stats-card shadow-sm">
      <div className="card-body">
        <div className="small text-muted text-uppercase">{title}</div>
        <div className={`h5 ${danger ? "text-danger" : ""}`}>
          {value || 0}
        </div>
      </div>
    </div>
  </div>
);


export default ApprovalsKYC;
